import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "dropdown", "option", "selectAll", "displayText", "chevron", "hiddenInput", "optionLabel"]
  static values = { 
    name: String,
    selected: String
  }

  connect() {
    this.selectedValues = this.selectedValue ? this.selectedValue.split(',').filter(v => v.trim()) : []
    this.updateDisplay()
    this.updateSelectAllState()
    
    // Close dropdown when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener('click', this.boundCloseOnClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.boundCloseOnClickOutside)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const isOpen = !this.dropdownTarget.classList.contains('hidden')
    
    if (isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    // Close any other open dropdowns first
    document.querySelectorAll('[data-multi-select-target="dropdown"]:not(.hidden)').forEach(dropdown => {
      if (dropdown !== this.dropdownTarget) {
        dropdown.classList.add('hidden')
      }
    })
    
    this.dropdownTarget.classList.remove('hidden')
    this.triggerTarget.setAttribute('aria-expanded', 'true')
    this.chevronTarget.style.transform = 'rotate(180deg)'
    
    // Focus first checkbox for keyboard navigation
    const firstCheckbox = this.dropdownTarget.querySelector('input[type="checkbox"]')
    if (firstCheckbox) {
      setTimeout(() => firstCheckbox.focus(), 100)
    }
  }

  close() {
    this.dropdownTarget.classList.add('hidden')
    this.triggerTarget.setAttribute('aria-expanded', 'false')
    this.chevronTarget.style.transform = 'rotate(0deg)'
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  updateSelection(event) {
    const checkbox = event.target
    const value = checkbox.value
    
    if (checkbox.checked) {
      // Add value if not already present
      if (!this.selectedValues.includes(value)) {
        this.selectedValues.push(value)
      }
    } else {
      // Remove value
      this.selectedValues = this.selectedValues.filter(v => v !== value)
    }
    
    this.updateDisplay()
    this.updateSelectAllState()
    this.updateHiddenInput()
    this.triggerChangeEvent()
  }

  toggleAll(event) {
    const selectAllChecked = event.target.checked
    
    this.optionTargets.forEach(checkbox => {
      checkbox.checked = selectAllChecked
      const value = checkbox.value
      
      if (selectAllChecked) {
        if (!this.selectedValues.includes(value)) {
          this.selectedValues.push(value)
        }
      } else {
        this.selectedValues = this.selectedValues.filter(v => v !== value)
      }
    })
    
    this.updateDisplay()
    this.updateHiddenInput()
    this.triggerChangeEvent()
  }

  updateSelectAllState() {
    if (this.hasSelectAllTarget) {
      const totalOptions = this.optionTargets.length
      const selectedOptions = this.optionTargets.filter(checkbox => checkbox.checked).length
      
      if (selectedOptions === 0) {
        this.selectAllTarget.checked = false
        this.selectAllTarget.indeterminate = false
      } else if (selectedOptions === totalOptions) {
        this.selectAllTarget.checked = true
        this.selectAllTarget.indeterminate = false
      } else {
        this.selectAllTarget.checked = false
        this.selectAllTarget.indeterminate = true
      }
    }
  }

  updateDisplay() {
    const count = this.selectedValues.length
    const totalOptions = this.optionTargets.length
    
    let displayText
    if (count === 0) {
      displayText = "Select options..."
    } else if (count === 1) {
      const selectedOption = this.optionTargets.find(opt => opt.value === this.selectedValues[0])
      const label = selectedOption ? this.getOptionLabel(selectedOption) : this.selectedValues[0]
      displayText = label
    } else if (count === totalOptions) {
      displayText = `All ${this.nameValue.toLowerCase()} selected`
    } else {
      displayText = `${count} ${this.nameValue.toLowerCase()} selected`
    }
    
    this.displayTextTarget.textContent = displayText
  }

  updateHiddenInput() {
    // Update hidden input for form submission
    this.hiddenInputTarget.value = this.selectedValues.join(',')
  }

  getOptionLabel(checkbox) {
    const label = checkbox.closest('[data-multi-select-target="optionLabel"]')
    return label ? label.textContent.trim() : checkbox.value
  }

  triggerChangeEvent() {
    // Dispatch a custom event to notify other controllers (like manual-filter)
    const event = new CustomEvent('multiSelectChange', {
      detail: {
        name: this.nameValue,
        values: this.selectedValues,
        element: this.element
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  // Keyboard navigation
  keydown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        this.close()
        this.triggerTarget.focus()
        break
      case 'Enter':
      case ' ':
        if (event.target === this.triggerTarget) {
          event.preventDefault()
          this.toggle(event)
        }
        break
      case 'ArrowDown':
        if (event.target === this.triggerTarget) {
          event.preventDefault()
          this.open()
        }
        break
    }
  }
}